/**
 * Partners Page
 *
 * Top-level page at /partners. Shows a tab nav with "You" and each
 * conversation partner. Selecting a tab displays the corresponding
 * memory card inline. Partner management (add/edit/delete) is accessible
 * via a gear button that toggles the SpeakerProfileManager.
 */

import { useState } from "react";
import { Brain, Settings, User, Users } from "lucide-react";
import { Layout } from "../components/Layout";
import { SelfMemoryPanel } from "../components/account/SelfMemoryPanel";
import { PartnerMemoryTab } from "../components/partners/PartnerMemoryTab";
import { SpeakerProfileManager } from "../components/speakers/SpeakerProfileManager";
import { useConversationPartners } from "../hooks/useConversationPartners";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "../components/ui/tabs";
import { Button } from "../components/ui/button";

export function PartnersPage() {
  const { data: partnersData } = useConversationPartners();
  const [showManager, setShowManager] = useState(false);

  const partners = partnersData?.partners || [];

  return (
    <Layout>
      <div className="container mx-auto max-w-4xl px-4 py-8">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <Brain className="h-6 w-6" />
              <h1 className="text-3xl font-bold text-foreground">Memories</h1>
            </div>
            <p className="mt-2 text-muted-foreground">
              The AI therapist&apos;s observations about each of you.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowManager(!showManager)}
            title="Manage partners"
          >
            <Settings className="mr-2 h-4 w-4" />
            Manage Partners
          </Button>
        </div>

        {showManager && (
          <div className="mb-6">
            <SpeakerProfileManager />
          </div>
        )}

        <Tabs defaultValue="you">
          <TabsList className="mb-4 w-full justify-start">
            <TabsTrigger value="you" className="gap-2">
              <User className="h-4 w-4" />
              You
            </TabsTrigger>
            {partners.map((partner) => (
              <TabsTrigger
                key={partner.id}
                value={partner.id}
                className="gap-2"
              >
                <Users className="h-4 w-4" />
                {partner.name}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="you">
            <SelfMemoryPanel />
          </TabsContent>

          {partners.map((partner) => (
            <TabsContent key={partner.id} value={partner.id}>
              <PartnerMemoryTab
                partnerId={partner.id}
                partnerName={partner.name}
              />
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </Layout>
  );
}
